import { Box, Collapse, ListItem, styled } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { Fragment, useCallback, useMemo } from 'react';
import { useSafeInteractionParams } from 'src/common/hooks/useSafeInteractionParams';
import { useURLHelpers } from 'src/common/hooks/useURLHelpers';
import { Link } from 'src/components/SideBar/components/Link';
import { OfferButtons } from 'src/components/SideBar/components/OfferButtons';
import { LINK_LABELS } from 'src/constants';
import {
  coverSheetNavSectionsState,
  openSideBarSectionsState,
} from 'src/state/navigation';
import { getSideBarLinkId } from 'src/utils/navigation';
import { useInitializeCoverSheetSideBar } from 'src/views/Coversheet/hooks/coverSheetSideBarHooks';
import { useInteraction } from '@/common/hooks/useInteraction';
import { useAppFlags } from '@/modules/launch-darkly/app-flags';
import { useContactMissingFields } from '@/views/Contacts/useContactMissingFields';
import {
  useIsCoversheetComplete,
  useMissingFieldsBySidebarLinkIds,
} from '@/views/Coversheet/state/coverSheetValidation';
import { useInteractionLockedContext } from '@/views/Interactions/context/InteractionLockedContext';
import { useSidebarLinks } from './useSidebarLinks/useSidebarLinks';

const SubLinkContainer = styled(Box)`
  margin-left: ${({ theme }) => theme.spacing(2)};
  border-left: ${({ theme }) =>
    `solid ${theme.typography.pxToRem(1)} ${theme.palette.grey[400]}`};
`;

interface SideBarContentProps {
  onCoversheetLinksClick?: () => void;
}

export const SideBarContent = ({
  onCoversheetLinksClick,
}: SideBarContentProps) => {
  const { pathname } = useRouter();
  const { interactionId, interactionType } = useSafeInteractionParams();

  const { data: interaction } = useInteraction(interactionId, interactionType);
  const { isLocked } = useInteractionLockedContext();

  const { txmCoverSheetContactsOffConfig } = useAppFlags();
  const hasUnrepresentedClient = !!interaction?.hasUnrepresentedClient;

  const [openSideBarSections, setOpenSideBarSections] = useAtom(
    openSideBarSectionsState,
  );
  const coverSheetNavSections = useAtomValue(
    coverSheetNavSectionsState(interactionId),
  );
  // Contacts-off: cover sheet is a plain "Property Info" link with no sublinks.
  const coverSheetSections = txmCoverSheetContactsOffConfig
    ? []
    : coverSheetNavSections;

  const coverSheetComplete = useIsCoversheetComplete(interactionId);
  const contactMissingFields = useContactMissingFields(
    interactionId,
    interactionType,
  );
  const contactsComplete = contactMissingFields.length === 0;
  const isInteractionValid = coverSheetComplete && contactsComplete;
  const missingFieldsBySidebarLinkIds =
    useMissingFieldsBySidebarLinkIds(interactionId);

  const toggleSideBarSections = useCallback(
    (label: string) => () =>
      setOpenSideBarSections((s) => ({ ...s, [label]: !s[label] })),
    [setOpenSideBarSections],
  );

  const allMissingFields = useMemo(
    () => Object.values(missingFieldsBySidebarLinkIds).flat(),
    [missingFieldsBySidebarLinkIds],
  );

  const { links } = useSidebarLinks();

  const isLinkCollapsed = (link: { id: string }) => {
    if (link.id === links.cover_sheet.id) {
      if (coverSheetSections.length) {
        if (openSideBarSections[LINK_LABELS.COVER_SHEET]) {
          // Coversheet link, children exist, link opened
          return false;
        }
        // Coversheet link, children exist, but link closed
        return true;
      }
      // Coversheet link, but no children loaded
      return undefined;
    }
    // Not Coversheet link
    return undefined;
  };
  const { isCurrentUrl: isOnCoverSheet } = useURLHelpers(
    links.cover_sheet?.pathname,
  );

  // In the locked/read-only state the per-field validators never mount (fields
  // render as read-only), so the missing-fields atom would stay empty and wrongly
  // enable the Documents link. Don't skip computation when locked, even on the
  // Cover Sheet page, so the atom is populated from data.
  useInitializeCoverSheetSideBar(
    interactionId,
    interactionType,
    isOnCoverSheet && !isLocked,
    hasUnrepresentedClient,
  );

  return (
    <>
      {Object.values(links).map((link) => (
        <Fragment key={link.id}>
          <Link
            interactionId={interactionId}
            label={link.label}
            path={link.pathname}
            collapsed={isLinkCollapsed(link)}
            disabled={
              (link.disabledWhenCoverSheetIncomplete && !isInteractionValid) ||
              (link.disabledWhenHasUnrepresentedClient &&
                hasUnrepresentedClient)
            }
            disabledTooltip={
              link.disabledWhenHasUnrepresentedClient && hasUnrepresentedClient
                ? link.unrepresentedClientTooltip
                : undefined
            }
            onClick={
              link.id === links.cover_sheet.id && coverSheetSections.length
                ? toggleSideBarSections(LINK_LABELS.COVER_SHEET)
                : undefined
            }
            CoverSheetLinkBadgeProps={{
              tooltip: `${link.label} missing required fields`,
              ...(link.id === links.cover_sheet.id && {
                count: allMissingFields.length || undefined,
                missingFieldsData: allMissingFields.length
                  ? allMissingFields
                  : undefined,
              }),
              ...(link.id === links.contacts?.id && {
                count: contactMissingFields.length || undefined,
                missingFieldsData: contactMissingFields.length
                  ? contactMissingFields
                  : undefined,
              }),
            }}
          />
          {link.id === links.cover_sheet.id && coverSheetSections.length ? (
            <Collapse in={openSideBarSections[LINK_LABELS.COVER_SHEET]}>
              <SubLinkContainer>
                {coverSheetSections.map((subLink) => {
                  const parentLink = {
                    ...links.cover_sheet,
                    path: links.cover_sheet?.pathname ?? '',
                    label: links.cover_sheet.label ?? '',
                  };
                  const sublinkId = getSideBarLinkId(interactionId, [
                    parentLink.label,
                    subLink.label,
                  ]);
                  return (
                    <Link
                      interactionId={interactionId}
                      key={`${link.label}-${subLink.label}`}
                      label={subLink.label}
                      path={subLink.path}
                      section={subLink.section}
                      parentLink={parentLink}
                      disabled={subLink.disabled}
                      onClick={
                        pathname.includes('cover-sheet')
                          ? onCoversheetLinksClick
                          : undefined
                      }
                      CoverSheetLinkBadgeProps={{
                        tooltip: `${subLink.label} missing required fields`,
                        count:
                          missingFieldsBySidebarLinkIds[sublinkId]?.length ??
                          undefined,
                        missingFieldsData:
                          missingFieldsBySidebarLinkIds[sublinkId] ?? undefined,
                      }}
                    />
                  );
                })}
              </SubLinkContainer>
            </Collapse>
          ) : null}
        </Fragment>
      ))}
      {(interactionType === 'offer' ||
        interaction?.representation === 'tenant') && (
        <ListItem
          key="offerButtons"
          sx={{ justifyContent: 'center', pt: 3, pb: 1 }}
        >
          <OfferButtons id={interactionId} type={interactionType} />
        </ListItem>
      )}
    </>
  );
};
